'use client';

import { userAtom } from '@/atoms/user';
import { useApiQuery } from '@/hooks/useApiQuery';
import { api } from '@/lib/api-client';
import { UserResponse } from '@/types/user';
import { usePrivy } from '@privy-io/react-auth';
import { useStatsigUser } from '@statsig/react-bindings';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';

const USER_QUERY_KEY = 'user';

export function useUser() {
  const { user: privyUser } = usePrivy();
  const queryClient = useQueryClient();
  const [userData, setUserData] = useAtom(userAtom);
  const [nickname, setNickname] = useState('');
  const { updateUserAsync } = useStatsigUser();

  const { data, isLoading, error } = useApiQuery<UserResponse>(
    [USER_QUERY_KEY, privyUser?.id ?? ''],
    async () => {
      const response = await api.getUser(privyUser!.id);
      if (response) setUserData(response);
      await updateUserAsync({ userID: response.user_id });
      console.log('userId', response.user_id);
      return response;
    },
    { enabled: !!privyUser?.id },
  );

  // Sync nickname with user?.name
  useEffect(() => {
    if (userData?.name && userData.name !== nickname) setNickname(userData.name);
  }, [userData?.name]);

  const invalidateUser = () => {
    queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY] });
  };

  // Use the atom data if available, otherwise use the query data
  const user = userData || data;

  // Update user name mutation
  const updateUserNameMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!privyUser?.id) throw new Error('No user ID');
      const res = await api.updateUser(privyUser.id, name);
      return res;
    },
    onSuccess: (response: UserResponse) => {
      if (response) setUserData(response);
      invalidateUser();
    },
  });

  const saveNickname = async () => {
    if (!nickname || nickname === user?.name) return;
    await updateUserNameMutation.mutateAsync(nickname);
  };

  return {
    user,
    isLoading,
    error,
    invalidateUser,
    nickname,
    setNickname,
    saveNickname,
    updating: updateUserNameMutation.isPending,
    updateError: updateUserNameMutation.error,
  };
}
